import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['predictiveSearchResults'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  getSearchInput() {
    if (window.innerWidth > 1200) {
      return document.querySelector("input[ref='searchInput'].desktop-search-bar");
    } else {
      return document.querySelector("input[ref='searchInput'].mobible-search-bar");
    }
  }

  getPredictiveSearchRsultsInner() {
    return this.querySelector("div[ref='predictiveSearchResults']");
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    const searchInput = this.refs.searchInput || this.getSearchInput();

    if (searchInput?.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    this.#prewarmOnFirstIntent();
  }

  /**
   * Pre-warm the empty/recently-viewed panel on the visitor's FIRST interaction rather than on
   * document load.
   *
   * This used to be onDocumentLoaded(() => this.resetSearch(false)), which ran on every page
   * load for a search panel nobody had opened. #resetSearch is not cheap: it fetches
   * ?section_id=predictive-search-empty through the Section Rendering API, parses the response
   * with DOMParser, reads RecentlyViewed from localStorage, optionally fetches a second
   * recently-viewed section, and morphs the result into the DOM. GTmetrix measured it at 158ms
   * CPU / 87ms script evaluation with a 114ms long task, plus a 4.1-4.7KB request, on pages
   * where search was never used.
   *
   * IMPORTANT - this deliberately does NOT defer the script or the custom element.
   * snippets/predictive-search-new.liquid documents why that fails: the header input
   * (#cmdk-input) drives this component from outside via the declarative
   * on:input="#predictiveSearchComponent/search" binding, which only attaches if the element is
   * already defined when the markup is parsed. DESPFY-325/331 deferred the script and killed
   * search on PLP and search pages. The element is still defined and upgraded exactly as before;
   * only this pre-warm moves.
   *
   * Bound to any first interaction rather than to the search field specifically, because that
   * binding is the part that proved fragile - the box shoppers type in lives outside this
   * component. Interacting with search IS an interaction, so this cannot miss it, and it fires
   * long before a shopper can type a query. A synthetic run never interacts, so it never pays.
   */
  #prewarmed = false;

  #prewarmOnFirstIntent = () => {
    const { signal } = this.#controller;

    const prewarm = () => {
      if (this.#prewarmed) return;
      this.#prewarmed = true;
      this.resetSearch(false); // Pass false to avoid focusing the input
    };

    /* focusin is deliberately NOT in this list. The Usercentrics banner auto-focuses itself
       on load (data-testid="uc-banner-content" tabindex="-1"), which fired focusin on every
       page view and defeated the deferral entirely - ?section_id=predictive-search-empty was
       still being requested on every GTmetrix run. perf-defer-script.js already excludes
       "focus" from its own interaction list for the same class of reason; this now matches.
       Real search use always involves a pointerdown, touchstart or keydown first. */
    for (const eventName of ['pointerdown', 'keydown', 'touchstart']) {
      document.addEventListener(eventName, prewarm, { once: true, passive: true, signal });
    }
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    const searchInput = this.refs.searchInput || this.getSearchInput();

    if (!isInteractiveElement && searchInput) {
      searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    const searchInput = this.refs.searchInput || this.getSearchInput();

    searchInput?.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const predictiveSearchResults = this.refs.predictiveSearchResults || this.getPredictiveSearchRsultsInner();
        const singleResultContainer = predictiveSearchResults.querySelector('[data-single-result-url]');

        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchInput = this.refs.searchInput || this.getSearchInput();
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', searchInput?.value || '');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    const searchInput = this.refs.searchInput || this.getSearchInput();

    if (keepFocus) {
      searchInput?.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchInput = this.refs.searchInput || this.getSearchInput();


    const searchTerm = searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm)
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const predictiveSearchResults = this.refs.predictiveSearchResults || this.getPredictiveSearchRsultsInner();

      const resultsInner = predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    if (+searchTerm) {
      url.searchParams.set('resources[options][fields]', 'variants.sku');
      url.searchParams.set('resources[options][unavailable_products]', 'show');
      url.searchParams.set('resources[type]', 'product');
    }

    if (this.dataset.sectionId === "predictive-search-new") {
      url.searchParams.set('resources[type]', 'product,query,collection');
    }

    let { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph((predictiveSearchResults || this.getPredictiveSearchRsultsInner()), resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      }).finally(() => {
        const parentDialogElement = this.closest(".search-modal-new");
  
        if (parentDialogElement) {
          const dialogSearchModal = parentDialogElement.closest("#search-modal");
  
          if (dialogSearchModal && "showPredictiveSearchDialog" in dialogSearchModal) {
            dialogSearchModal.showPredictiveSearchDialog();
  
            requestAnimationFrame(() => {
              const searchInput = this.refs.searchInput || this.getSearchInput();

              searchInput?.focus();
            })
          }
        }
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    if (resetButton) {
      resetButton.hidden = true;
    }
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    if (resetButton) {
      resetButton.hidden = false;
    }
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    if (this.closest(".search-modal-new")) {
      const parentDialogElement = document.querySelector("#search-modal");

      // Guard against a DialogComponent whose required "dialog" ref never populated: if its
      // <dialog ref="dialog"> child wasn't in the DOM yet when connectedCallback ran,
      // component.js's #updateRefs throws before assigning this.refs, so refs stays {} and the
      // MutationObserver that would otherwise pick the ref up later never gets installed either
      // (that happens later in the same connectedCallback, after the throw). closeDialog() then
      // reads refs.dialog.open on undefined and throws. Skip the call rather than crash the reset.
      if (parentDialogElement?.refs?.dialog) {
        parentDialogElement.closeDialog();
      }
    }

    const { predictiveSearchResults } = this.refs;
    const searchInput = this.refs.searchInput || this.getSearchInput();
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    if (searchInput) {
      searchInput.value = '';
    }
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
